import { Button } from '@/components/ui/button';
import { Camera, X, Loader2, FileText, Eye } from 'lucide-react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AuthenticatedImage } from './authenticated-image';
import { useCallback, useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface ResponsiveImageUploadProps {
  form: any;
  fieldName: string;
  label: string;
  description?: string;
  isUploading?: boolean;
  onCameraCapture: () => void;
  onFileUpload: () => void;
  onPhotoDeleted?: () => void;
  acceptPdf?: boolean;
}

export function ResponsiveImageUpload({
  form,
  fieldName,
  label,
  description,
  isUploading = false,
  onCameraCapture,
  onFileUpload,
  onPhotoDeleted,
  acceptPdf = false,
}: ResponsiveImageUploadProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [imageKey, setImageKey] = useState(0); // Force re-render of AuthenticatedImage
  const { toast } = useToast();

  // Watch the form field value
  const fieldValue = form.watch(fieldName);

  // Force re-render when field value changes
  useEffect(() => {
    setImageKey((prev) => prev + 1);
  }, [fieldValue]);

  const handleDeletePhoto = async (fieldValue: string) => {
    if (!fieldValue || isDeleting) return;

    setIsDeleting(true);

    try {
      // Check if it's a data URL (temporary image) or an API endpoint
      if (fieldValue.startsWith('data:')) {
        // For temporary images, just clear the field
      } else {
        // Extract photo ID from URL if it's an API endpoint
        const photoIdMatch = fieldValue.match(/\/photos\/(\d+)\/view/);

        if (photoIdMatch) {
          const photoId = photoIdMatch[1];
          const response = await fetch(`/api/site-surveys/photos/photo/${photoId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete photo');
          }
        }
      }

      // Clear the field value
      form.setValue(fieldName, '', {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });

      // Force re-render
      setImageKey((prev) => prev + 1);

      toast({
        title: 'Photo removed',
        description: 'The photo has been removed successfully.',
      });

      // Notify parent that photo was deleted
      if (onPhotoDeleted) {
        onPhotoDeleted();
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast({
        title: 'Delete failed',
        description:
          error instanceof Error ? error.message : 'Failed to delete photo. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };
  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem className="w-full">
          <FormLabel className="text-sm font-medium">{label}</FormLabel>

          {description && <p className="text-xs text-gray-500 mb-2">{description}</p>}

          <FormControl>
            <div className="w-full">
              {field.value ? (
                // Image/File preview
                <div className="relative w-full">
                  {field.value.toLowerCase().endsWith('.pdf') ? (
                    // PDF preview
                    <div className="w-full h-32 sm:h-48 bg-gray-50 rounded-lg border flex items-center justify-center">
                      <div className="text-center">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">PDF Document</p>
                        <p className="text-xs text-gray-500 mt-1">{field.value.split('/').pop()}</p>
                      </div>
                    </div>
                  ) : (
                    // Image preview with hover controls
                    <div className="relative group">
                      <AuthenticatedImage
                        key={`${field.value}-${imageKey}`} // Force re-render when URL changes or on manual trigger
                        src={field.value}
                        alt={label}
                        className="w-full h-32 sm:h-48 object-cover rounded-lg border"
                      />
                      {/* Action buttons overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 [@media(hover:none)]:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center pointer-events-none">
                        <div className="opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 flex space-x-2 transition-opacity duration-200 pointer-events-auto">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                className="shadow-lg hover:scale-105 transition-transform"
                                title="View full size"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl">
                              <DialogHeader>
                                <DialogTitle>{label}</DialogTitle>
                                <DialogDescription>Full size preview</DialogDescription>
                              </DialogHeader>
                              <div className="flex justify-center">
                                <AuthenticatedImage
                                  key={`preview-${field.value}-${imageKey}`}
                                  src={field.value}
                                  alt={label}
                                  className="max-w-full max-h-[70vh] object-contain rounded-md"
                                />
                              </div>
                            </DialogContent>
                          </Dialog>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                disabled={isDeleting}
                                className="shadow-lg hover:scale-105 transition-transform"
                                title="Remove photo"
                              >
                                {isDeleting ? (
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                ) : (
                                  <X className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove Photo</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to remove this photo? This action cannot be
                                  undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeletePhoto(field.value)}>
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Upload area
                <div className="w-full">
                  {isUploading ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Uploading...</p>
                    </div>
                  ) : (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center hover:border-gray-400 hover:bg-gray-50 transition-all cursor-pointer"
                      onClick={onFileUpload}
                    >
                      <div className="flex justify-center mb-3">
                        <div className="relative">
                          <Camera className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 font-medium mb-1">Tap to add photo</p>
                      <p className="text-xs text-gray-500">Take a photo or choose from gallery</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
